import {
  Cell,
  type ColumnConfig,
  Flex,
  Link,
  Table,
  Text,
} from '@backstage/ui';
import {
  CATEGORY_STATUS,
  type TeamCategorySummaryDto,
} from '@internal/scorecards-common';
import { useMemo } from 'react';

import { ScoreCircle } from '../ScoreCircle/ScoreCircle';
import { PassFailBar } from '../SegmentedBar/SegmentedBar';
import { severityDisplay } from '../../severity';
import { useSeverities } from '../../hooks/useSeverities';
import styles from './CategoriesTable.module.css';

const UNCATEGORIZED_ROW_ID = 'uncategorized';

interface CategoryRow {
  id: string;
  category: TeamCategorySummaryDto;
}

interface CategoriesTableProps {
  categories: TeamCategorySummaryDto[];
  projectsTotal: number;
  selectedCategoryIds?: Set<string>;
  onCategoryClick?: (id: string) => void;
}

export const CategoriesTable = ({
  categories,
  projectsTotal,
  selectedCategoryIds,
  onCategoryClick,
}: CategoriesTableProps) => {
  const { severities } = useSeverities();
  const orderedSeverities = useMemo(
    () => [...severities].sort((a, b) => a.rank - b.rank),
    [severities],
  );

  const rows: CategoryRow[] = useMemo(
    () =>
      categories.map(category => ({
        id: category.id ?? UNCATEGORIZED_ROW_ID,
        category,
      })),
    [categories],
  );

  const columnConfig: ColumnConfig<CategoryRow>[] = useMemo(
    () => [
      {
        id: 'score',
        label: 'Score (% checks passing)',
        cell: ({ category }) => {
          const strokeColor =
            category.status === CATEGORY_STATUS.BELOW_TARGET
              ? 'var(--zf-sc-fail)'
              : 'var(--zf-sc-pass)';
          return (
            <Cell>
              <Flex direction="column" gap="2" align="start">
                <ScoreCircle value={category.score} color={strokeColor} />
                {category.status === CATEGORY_STATUS.ON_TARGET && (
                  <span
                    className={`${styles.statusPill} ${styles.statusPillSuccess}`}
                  >
                    On target
                  </span>
                )}
                {category.status === CATEGORY_STATUS.BELOW_TARGET && (
                  <span
                    className={`${styles.statusPill} ${styles.statusPillDanger}`}
                  >
                    Below target
                  </span>
                )}
              </Flex>
            </Cell>
          );
        },
      },
      {
        id: 'category',
        label: 'Category',
        isRowHeader: true,
        cell: ({ category }) => (
          <Cell>
            <Flex direction="column" gap="1">
              {onCategoryClick && category.id !== null ? (
                <button
                  type="button"
                  className={`${styles.categoryName} ${styles.categoryButton}${
                    selectedCategoryIds?.has(category.id!)
                      ? ` ${styles.categoryButtonActive}`
                      : ''
                  }`}
                  onClick={() => onCategoryClick(category.id!)}
                >
                  {category.name}
                </button>
              ) : (
                <Text as="p" className={styles.categoryName}>
                  {category.name}
                </Text>
              )}
              {category.description && (
                <Text
                  as="p"
                  variant="body-small"
                  color="secondary"
                  className={styles.categoryDescription}
                >
                  {category.description}
                </Text>
              )}
              {category.docsUrl && (
                <Link
                  href={category.docsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body-small"
                >
                  How is this scored?
                </Link>
              )}
            </Flex>
          </Cell>
        ),
      },
      {
        id: 'projects',
        label: 'Projects',
        cell: ({ category }) => {
          const failingTotal = Object.values(category.failingBySeverity).reduce(
            (s, n) => s + n,
            0,
          );
          const passing = category.inScope - failingTotal;
          const worstSev = orderedSeverities.find(
            sv => (category.failingBySeverity[sv.id] ?? 0) > 0,
          );
          return (
            <Cell>
              <Flex direction="column" gap="2">
                <Text as="p">{failingTotal} failing</Text>
                {worstSev && (
                  <Text as="p" variant="body-small" color="secondary">
                    Worst severity:{' '}
                    <span
                      style={{
                        color: severityDisplay(worstSev).colorVar,
                      }}
                    >
                      {severityDisplay(worstSev).label}
                    </span>
                  </Text>
                )}
                <Text as="p" variant="body-small" color="secondary">
                  {category.inScope} of {projectsTotal} projects tracked
                </Text>
                <PassFailBar
                  passing={passing}
                  failing={failingTotal}
                  total={category.inScope}
                />
              </Flex>
            </Cell>
          );
        },
      },
    ],
    [projectsTotal, selectedCategoryIds, onCategoryClick, orderedSeverities],
  );

  return (
    <section aria-labelledby="team-categories-heading">
      <Flex direction="column" gap="4">
        <Flex direction="column" gap="1">
          <Text as="h2" id="team-categories-heading" variant="title-small">
            Scorecards
          </Text>
          <Text as="p" variant="body-small" color="secondary">
            Compliance score and project failure breakdown per category.
          </Text>
        </Flex>
        <Table
          columnConfig={columnConfig}
          data={rows}
          pagination={{ type: 'none' }}
          emptyState={
            <Text as="p" color="secondary">
              No categories with results for this team.
            </Text>
          }
        />
      </Flex>
    </section>
  );
};
